import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatStreamError, sendChatMessageStream, initChat, endLiveSession, sendLiveTyping, resetLiveTyping, ConversationNotFoundError } from '../api/chatClient';
import type { StreamEvent, InitChatResponse, LiveAttachment } from '../api/chatClient';
import { useAuth } from '../auth/useAuth';
import { useBrowserLiveOwner } from '../hooks/useBrowserLiveOwner';
import { isBrowserLiveConversation, canSubmitBrowserPreChatSurvey, getAnonSessionId, getRetainedBrowserConversationId, retainBrowserConversationId, clearRetainedBrowserConversationId, discardBrowserLiveConversation } from '../api/chatClient';
import { teamsExperience } from '../config/teamsExperience';
import { PreChatSurveyCard } from './PreChatSurveyCard';
import { AdaptiveCardBubble, SuggestedActionChips, SignInCard, cardSubmitToText } from './AdaptiveCardBubble';
import { LiveAttachmentChips } from './LiveAttachmentChips';
import { botTextToMarkdown, safeHttpUrl } from '../utils/botText';
import { extractSuggestedPrompts, resolveCitations, stripResolvedTag, stripSignalsForDisplay } from '../utils/chatSignals';
import type { Citation } from '../api/chatClient';
import { initialLivePresentation, legacyLivePresentation, LiveMessageGate, reduceLivePresentation, replaceLiveMessage, restoreLiveHistory, sourceMessageId, useVisibleLiveRead, isTerminalLiveFailure, isUncertainLiveSend, uncertainLiveSendIdentity, reconcileLiveSend, revealLiveSend, liveMessageScanFeedback } from '../../../../../../partner/widget/src/liveMessages';
import type { LivePresentationState, LiveRecoveryState } from '../../../../../../partner/widget/src/liveMessages';
import { EN_STRINGS } from '../../../../../../partner/widget/src/strings';
import { CaseDetailsMessage, readCaseDetails } from '../../../../../../partner/widget/src/CaseDetailsMessage';
import type { CmspCaseDetails } from '../../../../../../partner/widget/src/contract';
import { markBrowserLiveRead } from '../api/chatClient';

type Role = 'user' | 'agent' | 'system';
type Mode = 'ai' | 'live' | 'ended';
type FeedbackReason = 'new-chat' | 'live-ended';

interface Message {
  caseDetails?: CmspCaseDetails;
  clientOperationId?: string;
  deliveryUncertain?: boolean;
  pendingSend?: boolean;
  messageId?: string;
  id: string;
  role: Role;
  text: string;
  senderName?: string;
  isLive?: boolean;
  isBot?: boolean;
  readSequence?: number;
  readOn?: string;
  streaming?: boolean;
  sentAt?: number;
  receivedAt?: number;
  /** Rich message (activity-aware relay): raw Adaptive Card JSON. */
  liveCard?: unknown;
  /** Rich message: OAuth sign-in card from a bot (prompt + sign-in URL). */
  liveAuthCard?: { text: string; url: string; connectionName?: string };
  /** Rich message: suggested-action chips; clicking sends the value as a live message. */
  liveSuggestedActions?: Array<{ title: string; value: unknown; type: string }>;
  /** Files the live agent attached. Rendered as download chips; may be the message's only content. */
  liveAttachments?: LiveAttachment[];
  /** AI turn: choice pills parsed from a `[SUGGESTIONS: a | b | c]` signal. */
  suggestedPrompts?: string[];
  /** AI turn: citations parsed from a model-written "Sources" block (rare; we render inline links). */
  citations?: Citation[];
  /** AI turn: set when the agent requested human handoff (channelAction=handoff_to_omnichannel). */
  isHandoff?: boolean;
}

const QUICK_PROMPTS = [
  { label: 'Notifications not working', prompt: 'Teams notifications are not working' },
  { label: "Can't join calls", prompt: "I can't join Teams calls or meetings" },
  { label: 'Sign-in issues', prompt: "I can't sign in to Teams" },
  { label: 'Messages not sending', prompt: 'My Teams messages are not sending' },
  { label: 'Camera/mic problems', prompt: "Teams can't access my camera or microphone" },
];

// Transport/stream failures can carry a raw HTTP body — e.g. an Azure App Service
// "Error 403 - Forbidden" HTML page when the backend is down, restarting, or access-restricted.
// Never surface that verbatim in the chat: map it to a short, safe, human sentence.
function friendlyStreamError(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error ?? '');
  const status = raw.match(/\b(\d{3})\b/)?.[1];
  const looksLikeHtml = /<!doctype|<html|<\/?[a-z][\s\S]*>/i.test(raw);
  const unavailable = 'The support service is temporarily unavailable. Please try again in a moment.';
  switch (status) {
    case '401':
    case '403':
    case '500':
    case '502':
    case '503':
    case '504':
      return unavailable;
    case '408':
    case '429':
      return 'The support service is busy right now. Please wait a moment and try again.';
    default:
      // Opaque, oversized, or HTML/markup bodies must never reach the user verbatim.
      if (!raw || looksLikeHtml || raw.length > 160) {
        return 'Something went wrong. Please try again in a moment.';
      }
      return raw;
  }
}


/** Map IANA timezone to coarse geographic region hint for the API. */
function deriveCoarseRegion(): string {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  if (tz.startsWith('America/')) return 'us';
  if (tz.startsWith('Europe/')) return 'eu';
  if (tz.startsWith('Asia/') || tz.startsWith('Australia/') || tz.startsWith('Pacific/')) return 'apac';
  return 'unknown';
}

export function TeamsSupportPage() {
  const { account, isSignedIn, isAuthPending, acquireApiToken } = useAuth();
  const settledScopeRef = useRef<string | undefined>(undefined);
  if (!isAuthPending) {
    settledScopeRef.current = `teams.${encodeURIComponent(account?.homeAccountId ? `user:${account.homeAccountId}` : `anonymous:${getAnonSessionId()}`)}`;
  }
  const conversationScope = settledScopeRef.current;
  const conversationScopeRef = useRef(conversationScope);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'agent',
      text: teamsExperience.welcomeMessage,
    },
  ]);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  const [mode, setMode] = useState<Mode>('ai');
  const [recoveryState, setRecoveryState] = useState<LiveRecoveryState>('active');
  const [livePresentation, setLivePresentation] = useState<LivePresentationState>(initialLivePresentation);
  const liveSendBlocked = mode === 'live'
    && (recoveryState !== 'active' || livePresentation.phase === 'connecting' || livePresentation.phase === 'failed');
  const [conversationId, setConversationId] = useState<string | undefined>(() =>
    conversationScope ? getRetainedBrowserConversationId(conversationScope) : undefined);
  const [restoring, setRestoring] = useState(!!conversationId);
  const restoreGenerationRef = useRef(0);
  const completedResumeScopeRef = useRef<string | undefined>(undefined);
  useBrowserLiveOwner(conversationId);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const markRead = useCallback(async (id: string) => {
    if (conversationId) await markBrowserLiveRead(conversationId, id);
  }, [conversationId]);
  useVisibleLiveRead(scrollRef, messages, `${conversationScope}:${conversationId}`,
    mode === 'live' && !liveSendBlocked && !isAuthPending && !restoring
      && conversationScopeRef.current === conversationScope, markRead);
  const inputRef = useRef<HTMLInputElement>(null);
  const newChatRef = useRef<HTMLButtonElement>(null);
  const suggestedPromptsRef = useRef<HTMLDivElement>(null);
  // Tracks the last agent message whose choice pills we moved focus to, so we focus them exactly once
  // per response (not on every re-render).
  const focusedSuggestionsMsgIdRef = useRef<string | null>(null);
  // The pre-chat survey region, and a flag so we move focus to it exactly once when it appears.
  const preChatRegionRef = useRef<HTMLDivElement>(null);
  const preChatFocusedRef = useRef(false);
  const messagesRef = useRef<Message[]>([]);
  const liveMessageGateRef = useRef(new LiveMessageGate());
  const livePendingSentAtRef = useRef<number | null>(null);
  // Bounds automatic re-sends of a live message that hit the OC warm-up window (retryMessage).
  const liveResendCountRef = useRef(0);
  const [liveAgentName, setLiveAgentName] = useState<string | null>(null);
  // The shared transport selects the browser SDK or the unchanged server bridge.
  // These drive the live subscription and survey flow through that common interface.
  const serverLiveSessionIdRef = useRef<string | null>(null);
  const liveEventsAbortRef = useRef<AbortController | null>(null);
  // Latest conversation id, in a ref so the teardown paths can read it while the state that holds it is
  // being cleared around them.
  const conversationIdRef = useRef<string | undefined>(conversationId);
  useEffect(() => { conversationIdRef.current = conversationId; }, [conversationId]);
  // Most recently acquired API token, reused when a fresh one cannot be obtained — on sign-out
  // `isSignedIn` has already flipped false, and a bearer-less release is resolved by the server as
  // `anonymous:{conversationId}`, which fails the ownership check for a conversation a signed-in
  // customer started. See the same ref in ChatWidget.
  const lastBearerRef = useRef<string | null>(null);

  /**
   * Tells the server the customer has left so it releases the Omnichannel work item.
   *
   * This route is the one that needs it most: `teams-live-agent-direct` escalates straight to a human
   * with no AI turn, so a conversation abandoned here is always a queued work item. Without this the
   * page resets locally while the live chat stays open, and the orphan sits AHEAD of the customer's
   * next chat — an agent accepting from the queue gets a conversation nobody is watching.
   *
   * Safe on every teardown: the endpoint no-ops for a conversation with no bound session and is
   * idempotent.
   */
  const releaseLiveSession = useCallback(async () => {
    const convId = conversationIdRef.current;
    if (!convId) return;
    const fresh = isSignedIn ? await acquireApiToken().catch(() => null) : null;
    if (fresh) lastBearerRef.current = fresh;
    await endLiveSession(convId, fresh ?? lastBearerRef.current);
  }, [isSignedIn, acquireApiToken]);

  /**
   * Relays the customer's typing to the live agent's console. Outbound only — see ChatWidget and
   * LiveTypingService for why ACS cannot deliver the agent's own indicator to a polling server.
   */
  const notifyTyping = useCallback(() => {
    if (mode !== 'live') return;
    const convId = conversationIdRef.current;
    if (!convId) return;
    void sendLiveTyping(convId, lastBearerRef.current);
  }, [mode]);
  const [serverPreChatSurvey, setServerPreChatSurvey] = useState<{ survey: unknown; conversationId: string } | null>(null);
  const [submittingServerPreChat, setSubmittingServerPreChat] = useState(false);
  const preChatSubmissionRef = useRef(false);
  const [serverPreChatError, setServerPreChatError] = useState<{ text: string; canRetry: boolean } | null>(null);
  const [serverPostChatSurvey, setServerPostChatSurvey] = useState<{ url: string } | null>(null);
  const [feedbackReason, setFeedbackReason] = useState<FeedbackReason | null>(null);
  const [feedbackRating, setFeedbackRating] = useState(0);
  // Tracks the message whose text was just copied, to briefly swap the copy icon for a check.
  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null);
  const copiedResetRef = useRef<number | null>(null);
  // Dedicated polite live-region text. Updated ONLY when a message completes (onDone) or on error —
  // never on streaming token updates — so screen readers announce whole messages once instead of
  // re-announcing partial content on every chunk.
  const [liveAnnouncement, setLiveAnnouncement] = useState('');

  // Announce a completed status message. Clear-then-set on a later tick so that two identical
  // consecutive messages still register as a live-region change (and thus get re-announced).
  const announce = useCallback((text: string) => {
    if (!text) return;
    const generation = restoreGenerationRef.current;
    setLiveAnnouncement('');
    window.setTimeout(() => {
      if (generation === restoreGenerationRef.current) setLiveAnnouncement(text);
    }, 60);
  }, []);

  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Inject animation keyframes once for the typing indicator.
  useEffect(() => {
    const styleId = 'cmsp-chat-activity-style';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes cmsp-chat-bounce{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-8px)}}
      .teams-support-input:focus-visible{outline:2px solid ${teamsTheme.purple};outline-offset:2px}
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // Return focus to the text box once a response finishes rendering (pending → false) so the user
  // can keep typing without clicking back in. Runs after re-render, when the input is enabled again.
  useEffect(() => {
    if (!pending && mode !== 'ended') {
      inputRef.current?.focus();
    }
  }, [pending, mode]);

  // When the feedback card closes (Skip or after rating), return keyboard focus to the "+ New Chat"
  // trigger that opened it, rather than letting focus fall back to the composer. Runs after the
  // input-autofocus effect above so this wins on the same commit. Opening is handled inside FeedbackCard.
  const prevFeedbackReasonRef = useRef<FeedbackReason | null>(null);
  useEffect(() => {
    const prev = prevFeedbackReasonRef.current;
    prevFeedbackReasonRef.current = feedbackReason;
    if (prev && !feedbackReason) {
      newChatRef.current?.focus();
    }
  }, [feedbackReason]);

  useEffect(() => {
    return () => {
      if (conversationIdRef.current && conversationScopeRef.current) retainBrowserConversationId(conversationScopeRef.current, conversationIdRef.current);
      restoreGenerationRef.current++;
      abortRef.current?.abort();
      liveEventsAbortRef.current?.abort();
    };
  }, []);

  const appendSystemMessage = useCallback((text: string) => {
    setMessages(prev => {
      const recentDuplicate = prev
        .slice(-4)
        .some(message => message.role === 'system' && message.text === text);
      return recentDuplicate
        ? prev
        : [...prev, { id: crypto.randomUUID(), role: 'system', text }];
    });
    // System notices (live-agent transitions, stream errors surfaced here) are complete on arrival —
    // route them through the dedicated live region so they're announced without focus.
    announce(text);
  }, [announce]);

  const resetChat = useCallback(async (detach = false) => {
    const generation = restoreGenerationRef.current;
    // Release BEFORE the local state that identifies the conversation is cleared. Without this the page
    // resets itself while the live chat stays open and queued.
    const browserBound = conversationIdRef.current && isBrowserLiveConversation(conversationIdRef.current);
    if (browserBound && !detach) {
      try { await releaseLiveSession(); }
      catch (error) {
        if (generation !== restoreGenerationRef.current) return;
        appendSystemMessage(`${teamsExperience.errorPrefix} ${friendlyStreamError(error)}`);
        return;
      }
    } else if (!browserBound && !detach) void releaseLiveSession();
    if (generation !== restoreGenerationRef.current) return;
    // Drop this conversation's typing-throttle entry rather than retaining one per conversation for the
    // life of the page. Same reason as ChatWidget's resetChat.
    resetLiveTyping(conversationIdRef.current ?? undefined);
    if (conversationScopeRef.current) clearRetainedBrowserConversationId(conversationScopeRef.current);
    completedResumeScopeRef.current = conversationScopeRef.current;
    restoreGenerationRef.current++;
    setRestoring(false);
    conversationIdRef.current = undefined;
    abortRef.current?.abort();
    livePendingSentAtRef.current = null;
    // Stop the live subscribe (poll) stream and clear its state.
    liveEventsAbortRef.current?.abort();
    liveEventsAbortRef.current = null;
    serverLiveSessionIdRef.current = null;
    setServerPreChatSurvey(null);
    setSubmittingServerPreChat(false);
    preChatSubmissionRef.current = false;
    setServerPreChatError(null);
    setServerPostChatSurvey(null);
    liveMessageGateRef.current.reset();
    setMessages([{
      id: 'welcome',
      role: 'agent',
      text: teamsExperience.welcomeMessage,
    }]);
    setDraft('');
    setPending(false);
    setMode('ai');
    setRecoveryState('active');
    setLivePresentation(initialLivePresentation);
    setConversationId(undefined);
    setLiveAgentName(null);
    setFeedbackReason(null);
    setFeedbackRating(0);
    setLiveAnnouncement('');
    if (detach) lastBearerRef.current = null;
    inputRef.current?.focus();
  }, [releaseLiveSession, appendSystemMessage]);

  useEffect(() => {
    if (!isAuthPending && conversationScopeRef.current !== conversationScope) {
      // AI conversations also carry an owner-bound coordinator; never migrate their ID/history.
      void resetChat(true);
      conversationScopeRef.current = conversationScope;
    }
  }, [conversationScope, isAuthPending, resetChat]);

  const requestNewChat = useCallback(() => {
    const hasConversation = messages.some(message => message.id !== 'welcome');
    if (hasConversation || mode !== 'ai') {
      setFeedbackReason('new-chat');
      setFeedbackRating(0);
      return;
    }

    resetChat();
  }, [messages, mode, resetChat]);

  const completeFeedback = useCallback((rating?: number) => {
    if (rating) setFeedbackRating(rating);
    const shouldStartNewChat = feedbackReason === 'new-chat';
    setFeedbackReason(null);
    setFeedbackRating(0);

    if (shouldStartNewChat) {
      resetChat();
    }
  }, [feedbackReason, resetChat]);

  const handleCopy = useCallback((messageId: string) => {
    const msg = messagesRef.current.find(m => m.id === messageId);
    if (!msg) return;
    const text = (msg.text ?? '').trim();
    if (!text) return;
    const markCopied = () => {
      setCopiedMsgId(messageId);
      if (copiedResetRef.current) window.clearTimeout(copiedResetRef.current);
      copiedResetRef.current = window.setTimeout(() => setCopiedMsgId(null), 1500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(markCopied).catch(() => {
        // eslint-disable-next-line no-console
        console.error('Copy to clipboard failed');
      });
    } else {
      // Fallback for insecure contexts / older browsers without the async clipboard API.
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        markCopied();
      } catch {
        // eslint-disable-next-line no-console
        console.error('Copy to clipboard failed');
      }
    }
  }, []);

  useEffect(() => () => {
    if (copiedResetRef.current) window.clearTimeout(copiedResetRef.current);
  }, []);

  const appendLiveToken = useCallback((evt: StreamEvent) => {
    const scanFeedback = liveMessageScanFeedback(evt, EN_STRINGS);
    if (scanFeedback) {
      if (evt.clientOperationId) {
        if (!liveMessageGateRef.current.remember(`scan:${evt.clientOperationId}`)) return;
        const unsent = messagesRef.current.find(message =>
          message.clientOperationId === evt.clientOperationId && message.role === 'user');
        setMessages(previous => previous.filter(message => message.clientOperationId !== evt.clientOperationId));
        if (unsent) setDraft(current => current || unsent.text);
      }
      appendSystemMessage(scanFeedback);
      return;
    }
    if (isUncertainLiveSend(evt) && evt.clientOperationId) {
      setMessages(previous => previous.map(message => message.clientOperationId === evt.clientOperationId
        ? { ...message, ...uncertainLiveSendIdentity(evt, message) } : message));
      return;
    }
    if (evt.senderKind === 'system' && evt.liveCategory === 'message-sent') {
      setMessages(previous => reconcileLiveSend(previous, evt));
    }
    if (evt.recoveryState) setRecoveryState(evt.recoveryState);
    setLivePresentation(previous => reduceLivePresentation(previous, evt));
    if (evt.senderKind === 'system' && ['capabilities', 'read-receipt', 'message-sent'].includes(evt.liveCategory ?? '')) {
      return;
    }
    if (evt.messageUpdated) {
      setMessages(previous => replaceLiveMessage(previous, evt.messageId, {
        text: evt.text ? botTextToMarkdown(evt.text) : '',
        senderName: evt.senderName?.trim() || undefined,
        liveAttachments: evt.attachments?.length ? evt.attachments : undefined,
      }));
      return;
    }
    const accepted = liveMessageGateRef.current.accept(evt);
    if (!accepted) return;
    const attachments = evt.attachments ?? [];
    // The agent's name rides on their own messages and on the agent-joined notice — the latter is the
    // only frame that carries it before they speak, so capture it before the empty-frame bail below.
    const sender = evt.senderName?.trim();
    // See ChatWidget: a transfer retires the outgoing agent's name immediately rather than letting it
    // linger until the next agent happens to speak.
    if (accepted.agentName !== undefined) setLiveAgentName(accepted.agentName);
    // A file-only message carries no text; bailing on empty text here would drop it entirely.
    if (!accepted.show || (!evt.text && attachments.length === 0)) return;
    if (evt.senderKind === 'human' || evt.senderKind === 'bot') {
      const receivedAt = Date.now();
      const sentAt = livePendingSentAtRef.current ?? undefined;
      livePendingSentAtRef.current = null;
      setMessages(prev => [...prev, {
        id: sourceMessageId(evt.messageId) ?? crypto.randomUUID(), messageId: sourceMessageId(evt.messageId),
        role: 'agent', text: evt.text ? botTextToMarkdown(evt.text) : '',
        isLive: evt.senderKind === 'human', isBot: evt.senderKind === 'bot',
        sentAt, receivedAt: sentAt ? receivedAt : undefined,
        senderName: sender || undefined,
        liveAttachments: attachments.length > 0 ? attachments : undefined,
      }]);
    } else if (evt.text) {
      setMessages(prev => [...prev, {
        id: sourceMessageId(evt.messageId) ?? crypto.randomUUID(), messageId: sourceMessageId(evt.messageId),
        role: 'system', text: evt.text!,
      }]);
    }
  }, [appendSystemMessage]);

  // Credentials for an attachment download, resolved at click time rather than captured at render, so a
  // token that expires while the chat sits open does not break a late download.
  const getAttachmentAuth = useCallback(async () => {
    const bearer = isSignedIn ? await acquireApiToken().catch(() => null) : null;
    return {
      authBearer: bearer ?? undefined,
      homeAccountId: account?.homeAccountId,
      username: account?.username,
    };
  }, [isSignedIn, acquireApiToken, account]);

  // Terminal is true when the human agent ended the chat: the WHOLE conversation is over (server closed it)
  // and does NOT return to the AI — the composer is disabled and only "New Chat" continues. Transient ends
  // (session-gone) pass terminal=false and fall back to the AI so the customer can resend.
  const handleLiveEnded = useCallback((evt: StreamEvent, terminal: boolean) => {
    const accepted = liveMessageGateRef.current.accept({ ...evt, liveCategory: 'ended' });
    if (accepted?.show) {
      setMessages(prev => [...prev, {
        id: sourceMessageId(evt.messageId) ?? crypto.randomUUID(), messageId: sourceMessageId(evt.messageId),
        role: 'system', text: evt.text || 'The live agent has ended the conversation.',
      }]);
    }
    const surveyUrl = safeHttpUrl(evt.postChatSurveyUrl);
    if (surveyUrl) setServerPostChatSurvey({ url: surveyUrl });
    serverLiveSessionIdRef.current = null;
    liveEventsAbortRef.current?.abort();
    liveEventsAbortRef.current = null;
    setMode(terminal ? 'ended' : 'ai');
    setLiveAgentName(null);
    setFeedbackReason('live-ended');
    setFeedbackRating(0);
  }, [appendSystemMessage]);

  // One "poll turn": an empty /chat/stream request the server drains the live thread for, returning any
  // new agent/lifecycle frames as mode="live" tokens. Resolves with the outcome: the session ended, a
  // pre-chat survey must be shown before the session starts, or keep polling.
  const runLivePollTurn = useCallback((liveConversationId: string, signal: AbortSignal) =>
    new Promise<'ended' | 'survey' | 'continue' | 'failed'>((resolve) => {
      void (isSignedIn ? acquireApiToken().catch(() => null) : Promise.resolve(null)).then((bearer) => {
        if (signal.aborted) { resolve('continue'); return; }
        // Keep the sign-out fallback credential fresh: by the time sign-out tears the conversation
        // down there is no account left to acquire one from.
        if (bearer) lastBearerRef.current = bearer;
        let outcome: 'ended' | 'survey' | 'continue' = 'continue';
        const ctrl = sendChatMessageStream(
          {
            message: '', conversationId: liveConversationId, authBearer: bearer, poll: true,
            homeAccountId: account?.homeAccountId, username: account?.username, displayName: account?.name,
          },
          {
            onLiveToken: (e: StreamEvent) => {
              if (!signal.aborted) appendLiveToken(e);
              if (isTerminalLiveFailure(e)) { ctrl.abort(); resolve('failed'); }
            },
            onDone: (e: StreamEvent) => {
              if (signal.aborted) { resolve('continue'); return; }
              if (e.mode === 'ended') { outcome = 'ended'; handleLiveEnded(e, true); }
              else if (e.mode === 'ai') { outcome = 'ended'; handleLiveEnded(e, false); }
              else if (e.mode === 'prechat-survey' && e.preChatSurvey) {
                outcome = 'survey';
                setServerPreChatSurvey({ survey: e.preChatSurvey, conversationId: liveConversationId });
              }
              resolve(outcome);
            },
            onError: (error) => {
              if (!signal.aborted && isBrowserLiveConversation(liveConversationId)) {
                if (isTerminalLiveFailure(error)) {
                  setRecoveryState('failed');
                  setLivePresentation(previous => reduceLivePresentation(previous, {
                    liveCategory: 'error', recoveryState: 'failed',
                  }));
                }
                appendSystemMessage(`${teamsExperience.errorPrefix} ${friendlyStreamError(error)}`);
              }
              resolve(isTerminalLiveFailure(error) ? 'failed' : 'continue');
            },
          },
        );
        signal.addEventListener('abort', () => ctrl.abort(), { once: true });
      });
    }), [isSignedIn, acquireApiToken, account, appendLiveToken, handleLiveEnded]);

  // Browser sessions attach a continuous SDK subscription; legacy sessions retain their poll loop.
  const enterServerLiveMode = useCallback((
    sessionId: string,
    liveConversationId: string,
    restoredPresentation?: LivePresentationState,
  ) => {
    if (conversationScopeRef.current) retainBrowserConversationId(conversationScopeRef.current, liveConversationId);
    if (serverLiveSessionIdRef.current === sessionId && liveEventsAbortRef.current) return;
    liveEventsAbortRef.current?.abort();
    serverLiveSessionIdRef.current = sessionId;
    setServerPreChatSurvey(null);
    setSubmittingServerPreChat(false);
    setServerPreChatError(null);
    const browserLive = isBrowserLiveConversation(liveConversationId);
    setLivePresentation(browserLive ? restoredPresentation ?? initialLivePresentation : legacyLivePresentation);
    setRecoveryState(browserLive && (!restoredPresentation || restoredPresentation.phase === 'connecting')
      ? 'recovering'
      : restoredPresentation?.phase === 'failed' ? 'failed' : 'active');
    setMode('live');

    const loop = new AbortController();
    liveEventsAbortRef.current = loop;
    void (async () => {
      while (!loop.signal.aborted && serverLiveSessionIdRef.current === sessionId) {
        const result = await runLivePollTurn(liveConversationId, loop.signal);
        if (result === 'ended' || result === 'failed' || loop.signal.aborted) break;
        if (result === 'survey') {
          // A pre-chat survey must be answered before the (re)start. Tear down THIS poll loop and clear
          // the session binding so submitting the survey starts a FRESH loop — even if the server returns
          // the same sessionId (otherwise the "already live" guard early-returns and the chat freezes).
          if (liveEventsAbortRef.current === loop) liveEventsAbortRef.current = null;
          if (serverLiveSessionIdRef.current === sessionId) serverLiveSessionIdRef.current = null;
          loop.abort();
          break;
        }
        await new Promise<void>((r) => {
          const t = setTimeout(r, 3000);
          loop.signal.addEventListener('abort', () => { clearTimeout(t); r(); }, { once: true });
        });
      }
    })();
  }, [runLivePollTurn]);

  const resumeDependenciesRef = useRef({ account, isSignedIn, acquireApiToken, enterServerLiveMode, appendSystemMessage });
  resumeDependenciesRef.current = { account, isSignedIn, acquireApiToken, enterServerLiveMode, appendSystemMessage };
  useEffect(() => {
    if (isAuthPending || !conversationScope || completedResumeScopeRef.current === conversationScope) return;
    const { account, isSignedIn, acquireApiToken, enterServerLiveMode, appendSystemMessage } = resumeDependenciesRef.current;
    const retainedId = getRetainedBrowserConversationId(conversationScope);
    if (!retainedId) {
      completedResumeScopeRef.current = conversationScope;
      return;
    }
    const generation = ++restoreGenerationRef.current;
    let detached = false;
    const current = () => !detached && generation === restoreGenerationRef.current;
    conversationScopeRef.current = conversationScope;
    conversationIdRef.current = retainedId;
    setConversationId(retainedId);
    setRestoring(true);
    void (async () => {
      try {
        const bearer = isSignedIn ? await acquireApiToken() : null;
        if (!current()) return;
        const initialized = await initChat({
          conversationId: retainedId,
          sourceAppName: 'Teams Support', callerAppId: 'cmsp-consumer-teams',
          locale: navigator.language || 'en-US', region: deriveCoarseRegion(),
          isAuthenticated: isSignedIn, homeAccountId: account?.homeAccountId,
          email: account?.username, displayName: account?.name, entryPoint: teamsExperience.entryPoint,
        }, bearer);
        if (!current()) return;
        // A retained id that no conversation ever used comes back replaced by a server-created one.
        const resumedId = initialized.conversationId || retainedId;
        if (resumedId !== retainedId) {
          conversationIdRef.current = resumedId;
          setConversationId(resumedId);
        }
        liveMessageGateRef.current.reset();
        const restored = restoreLiveHistory(liveMessageGateRef.current, initialized.history ?? []);
        const restoredPresentation = (initialized.history ?? []).reduce((current, row) =>
          reduceLivePresentation(current, {
            messageId: row.messageId,
            senderName: row.senderName,
            senderKind: row.isLiveAgent ? 'human' : row.senderKind === 'bot' ? 'bot' : 'system',
            liveCategory: row.liveCategory,
            recoveryState: row.liveCategory === 'connecting' ? 'recovering'
              : row.liveCategory === 'waiting' ? 'active' : undefined,
          }), initialLivePresentation);
        setMessages([{
          id: 'welcome', role: 'agent', text: teamsExperience.welcomeMessage,
        }, ...restored.messages.map(message => ({
          id: sourceMessageId(message.messageId) ?? crypto.randomUUID(),
          messageId: sourceMessageId(message.messageId),
          role: (message.role === 'user' || message.role === 'system' ? message.role : 'agent') as Role,
          text: message.role === 'agent' ? botTextToMarkdown(message.bodyText ?? message.text) : message.text,
          caseDetails: message.role === 'agent' ? readCaseDetails(message.caseDetails) : undefined,
          isLive: message.isLiveAgent,
          isBot: message.senderKind === 'bot',
          readSequence: message.readSequence, readOn: message.readOn,
          senderName: message.senderName ?? undefined,
          citations: message.citations ?? undefined,
          liveAttachments: message.attachments,
        }))]);
        setLiveAgentName(restored.agentName);
        if (initialized.mode === 'ended') {
          clearRetainedBrowserConversationId(conversationScope);
          setMode('ended');
        } else if (initialized.mode === 'live' || isBrowserLiveConversation(resumedId)) {
          enterServerLiveMode(initialized.sessionId ?? resumedId, resumedId, restoredPresentation);
        }
      } catch (error) {
        if (current() && error instanceof ConversationNotFoundError) {
          // The retained conversation belongs to another account (e.g. signed in when it was created). It is
          // not ours to resume, so forget it and let the next message start a fresh chat.
          if (isBrowserLiveConversation(retainedId)) discardBrowserLiveConversation(retainedId);
          clearRetainedBrowserConversationId(conversationScope);
          conversationIdRef.current = undefined;
          setConversationId(undefined);
          setMessages([{ id: 'welcome', role: 'agent', text: teamsExperience.welcomeMessage }]);
        } else if (current()) {
          // Teams-local policy — deliberately NOT routed through the shared live-resume / escalation
          // code so a change there can never alter this behaviour. The Teams Omnichannel workstream has
          // "Reconnect to previous chat" turned OFF (Teams chat is unauthenticated), so a browser
          // Omnichannel SDK live session cannot be restored after a page reload — the resume init throws
          // (e.g. "restore: reconnect_not_enabled"). Re-entering live mode here only re-attempts the
          // impossible restore and leaves the composer permanently disabled. Drop the stale live binding,
          // keep the already-restored transcript visible, and present the chat as ended so the customer
          // can start a New Chat instead of being trapped.
          if (isBrowserLiveConversation(retainedId)) {
            discardBrowserLiveConversation(retainedId);
            clearRetainedBrowserConversationId(conversationScope);
            // Drop the local conversation handle too, so New Chat / resetChat does not treat this as a
            // still-bound live session and try to release it (which would hit the same reconnect failure
            // and leave the composer disabled).
            conversationIdRef.current = undefined;
            appendSystemMessage(teamsExperience.liveSessionNotResumableMessage);
            setMode('ended');
          } else {
            appendSystemMessage(`${teamsExperience.errorPrefix} ${friendlyStreamError(error)}`);
            setMode('ended');
          }
        }
      } finally {
        if (current()) {
          completedResumeScopeRef.current = conversationScope;
          setRestoring(false);
        }
      }
    })();
    return () => { detached = true; };
  }, [conversationScope, isAuthPending]);

  // Confirm the persisted handoff through the same init route as direct clients, supplying the summary.
  // On failure the negotiated live transport remains authoritative; a browser session cannot fall back.
  const callEscalationInit = useCallback(async (
    liveConversationId: string,
    transcript: Message[],
  ): Promise<InitChatResponse | null> => {
    // NOTE: do NOT early-return when the conversation is already browser-live-bound. The shared
    // Omnichannel coordinator binds the conversation synchronously (observe() on the
    // handoff_to_omnichannel done event) BEFORE this runs, so isBrowserLiveConversation() is already
    // true at the initial handoff. Skipping here would drop the action:"escalation" init that
    // establishes the server-side human-handoff binding (State=Handoff + ActiveAgentName), so the
    // subsequent browser-SDK /omnichannel/bootstrap fails with human_handoff_required (surfaced to the
    // user as "bootstrap: session_conflict"). The init is idempotent — on an already-established handoff
    // it returns the existing binding — so running it at the handoff transition is safe.
    if (!liveConversationId) return null;
    const generation = restoreGenerationRef.current;
    try {
      const bearer = isSignedIn ? await acquireApiToken().catch(() => null) : null;
      if (generation !== restoreGenerationRef.current) return null;
      const summary = JSON.stringify(transcript.filter(m => !m.pendingSend).map(m => ({
        role: m.role,
        text: m.text,
        sender: m.senderName ?? (m.role === 'agent' ? teamsExperience.agentName : m.role === 'user' ? teamsExperience.userName : 'System'),
        at: new Date(m.receivedAt ?? m.sentAt ?? Date.now()).toISOString(),
      })));

      return await initChat(
        {
          conversationId: liveConversationId,
          sourceAppName: 'Teams Support',
          callerAppId: 'cmsp-consumer-teams',
          // Teams for Life is en-us-only, so the escalation locale is always en-US.
          locale: 'en-US',
          region: deriveCoarseRegion(),
          isAuthenticated: isSignedIn,
          homeAccountId: account?.homeAccountId,
          email: account?.username,
          displayName: account?.name,
          entryPoint: teamsExperience.entryPoint,
          summary,
          action: 'escalation',
        },
        bearer,
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[teamsSupportPage] escalation initChat failed; continuing the negotiated live transport:', err);
      return null;
    }
  }, [acquireApiToken, isSignedIn, account]);

  // The common transport submits answers to the negotiated browser SDK or legacy bridge.
  // Keep the card mounted so failed submissions do not discard the customer's answers.
  const submitServerPreChatSurvey = useCallback(async (response: Record<string, unknown>) => {
    const pendingSurvey = serverPreChatSurvey;
    if (!pendingSurvey || preChatSubmissionRef.current || isAuthPending || serverPreChatError?.canRetry === false) return;
    const generation = restoreGenerationRef.current;
    const current = () => generation === restoreGenerationRef.current;
    preChatSubmissionRef.current = true;
    setSubmittingServerPreChat(true);
    setServerPreChatError(null);
    // Announce the post-submit "updating" state to assistive tech: the fieldset becomes disabled and a
    // spinner appears, but that visual-only change is otherwise silent to screen readers (6731547/6731576).
    announce('Submitting your answers, please wait…');
    try {
      const authBearer = isSignedIn ? await acquireApiToken() : null;
      if (!current()) return;
      await new Promise<void>((resolve, reject) => {
        const ctrl = sendChatMessageStream(
          {
            message: JSON.stringify(response), conversationId: pendingSurvey.conversationId, authBearer,
            homeAccountId: account?.homeAccountId, username: account?.username, displayName: account?.name,
          },
          {
            onLiveToken: (e: StreamEvent) => { if (current()) appendLiveToken(e); },
            onDone: (e: StreamEvent) => {
              if (!current()) { resolve(); return; }
              setServerPreChatSurvey(null);
              if (e.mode === 'ended') { handleLiveEnded(e, true); }
              else if (e.mode === 'ai') { handleLiveEnded(e, false); }
              else { enterServerLiveMode(e.sessionId ?? pendingSurvey.conversationId, pendingSurvey.conversationId); }
              resolve();
            },
            onError: (err) => reject(err),
          },
        );
        abortRef.current = ctrl;
        ctrl.signal.addEventListener('abort', () => resolve(), { once: true });
      });
    } catch (error) {
      if (!current()) return;
      if (isBrowserLiveConversation(pendingSurvey.conversationId)) {
        // The SDK memoizes start (including rejection). Only pre-start failures can be resubmitted.
        const canRetry = canSubmitBrowserPreChatSurvey(pendingSurvey.conversationId);
        const text = canRetry
          ? 'We could not submit your pre-chat answers. Your answers have been kept. Please try again.'
          : 'We could not confirm the live chat connection. Your answers have been kept. Use New Chat to try again; this request will not be sent again.';
        setRecoveryState(canRetry ? 'recovering' : 'failed');
        setLivePresentation(reduceLivePresentation(initialLivePresentation, canRetry
          ? { liveCategory: 'connecting', recoveryState: 'recovering' }
          : { liveCategory: 'error', recoveryState: 'failed' }));
        setMode('live');
        setServerPreChatError({ text, canRetry });
        announce(text);
        return;
      } else if (error instanceof ChatStreamError && error.code === 'live_agent_unavailable') {
        setServerPreChatSurvey(pendingSurvey);
      } else {
        setServerPreChatSurvey(null);
        setMode('ai');
      }
      appendSystemMessage(`${teamsExperience.errorPrefix} ${friendlyStreamError(error)}`);
    } finally {
      if (current()) {
        preChatSubmissionRef.current = false;
        setSubmittingServerPreChat(false);
      }
    }
  }, [serverPreChatSurvey, serverPreChatError, isAuthPending, isSignedIn, acquireApiToken, account, appendLiveToken, handleLiveEnded, enterServerLiveMode, appendSystemMessage, announce]);

  const send = useCallback(async (overrideText?: string) => {
    const text = (overrideText ?? draft).trim();
    if (!text || pending || restoring || isAuthPending || !conversationScope || mode === 'ended' || liveSendBlocked || serverPreChatSurvey || submittingServerPreChat) return;
    const generation = restoreGenerationRef.current;

    const sentAt = Date.now();
    const userMessageId = crypto.randomUUID();
    setDraft('');
    setPending(true);
    setMessages(prev => [...prev, {
      id: userMessageId, clientOperationId: userMessageId, role: 'user', text, sentAt,
      pendingSend: mode === 'live' && !!conversationId && isBrowserLiveConversation(conversationId),
    }]);

    if (mode === 'live' && serverLiveSessionIdRef.current) {
      // Server-side live mode: forward the message to the human agent through the SAME /chat/stream
      // endpoint (it routes to the Omnichannel agent). The SPA only ever calls init + stream — no OC endpoints.
      if (!conversationId) {
        // No conversation id → the server would mint a new one and reject the call; don't send blind.
        appendSystemMessage('Cannot send right now — the conversation is not fully initialized. Please try again in a moment.');
        setPending(false);
        inputRef.current?.focus();
        return;
      }
      livePendingSentAtRef.current = sentAt;
      const bearer = isSignedIn ? await acquireApiToken().catch(() => null) : null;
      if (generation !== restoreGenerationRef.current) return;
      abortRef.current = sendChatMessageStream(
        {
          message: text, clientOperationId: userMessageId, conversationId, authBearer: bearer,
          homeAccountId: account?.homeAccountId, username: account?.username, displayName: account?.name,
        },
        {
          onLiveToken: (e: StreamEvent) => { if (generation === restoreGenerationRef.current) appendLiveToken(e); },
          onDone: (e: StreamEvent) => {
            if (generation !== restoreGenerationRef.current) return;
            if (e.mode !== 'prechat-survey' && !e.retryMessage) {
              setMessages(previous => revealLiveSend(previous, userMessageId));
            }
            if (e.mode === 'prechat-survey' && e.preChatSurvey) {
              setServerPreChatSurvey({ survey: e.preChatSurvey, conversationId });
            }
            if (e.mode === 'ended') { handleLiveEnded(e, true); }
            else if (e.mode === 'ai') { handleLiveEnded(e, false); }
            else if (e.retryMessage && liveResendCountRef.current < 5) {
              // Not delivered (OC session warming on another replica) — re-send once after a short back-off.
              liveResendCountRef.current += 1;
              const resendText = text;
              setTimeout(() => { if (generation === restoreGenerationRef.current && serverLiveSessionIdRef.current) void send(resendText); }, 1500);
            }
            else { liveResendCountRef.current = 0; }
            setPending(false); inputRef.current?.focus();
          },
          onError: (err: Error) => {
            if (generation !== restoreGenerationRef.current) return;
            if (isTerminalLiveFailure(err)) {
              setRecoveryState('failed');
              setLivePresentation(previous => reduceLivePresentation(previous, {
                liveCategory: 'error', recoveryState: 'failed',
              }));
            }
            if (isUncertainLiveSend(err)) {
              setMessages(prev => prev.map(message => message.id === userMessageId
                ? { ...message, ...uncertainLiveSendIdentity(err, message) } : message));
            }
            if (isBrowserLiveConversation(conversationId) && !isUncertainLiveSend(err)) {
              setMessages(prev => prev.filter(message => message.role !== 'user' || message.sentAt !== sentAt));
              setDraft(current => current || text);
            }
            if (!isUncertainLiveSend(err)) appendSystemMessage(
              liveMessageScanFeedback(err, EN_STRINGS) ?? `${teamsExperience.errorPrefix} ${friendlyStreamError(err)}`,
            );
            setPending(false); inputRef.current?.focus();
          },
        },
      );
      return;
    }

    const replyId = crypto.randomUUID();
    let accumulatedReply = '';
    setMessages(prev => [...prev, { id: replyId, role: 'agent', text: '', streaming: true, sentAt }]);

    const authBearer = isSignedIn ? await acquireApiToken() : null;
    if (generation !== restoreGenerationRef.current) return;
    let effectiveConversationId = conversationId;
    if (!effectiveConversationId) {
      conversationScopeRef.current = conversationScope;
      try {
        // A new chat sends no id: chat/init issues it, and the stream below uses the one it returns.
        const initialized = await initChat({
          sourceAppName: 'Teams Support', callerAppId: 'cmsp-consumer-teams',
          locale: navigator.language || 'en-US', region: deriveCoarseRegion(),
          isAuthenticated: isSignedIn, homeAccountId: account?.homeAccountId,
          email: account?.username, displayName: account?.name, entryPoint: teamsExperience.entryPoint,
        }, authBearer);
        // The server issues the conversation id (AB#6752065): everything from here uses the one it returned.
        effectiveConversationId = initialized.conversationId;
        conversationIdRef.current = effectiveConversationId;
        setConversationId(effectiveConversationId);
      } catch {
        // chat/init did not start a conversation, so there is no id to keep: a send that finds an id already set
        // skips init for good (AB#6752065). Put the message back so the customer can send it again.
        if (generation !== restoreGenerationRef.current) return;
        conversationIdRef.current = undefined;
        setConversationId(undefined);
        setMessages(prev => prev.filter(message => message.id !== userMessageId && message.id !== replyId));
        setDraft(current => current || text);
        appendSystemMessage('We could not start the chat. Please try again.');
        setPending(false);
        inputRef.current?.focus();
        return;
      }
    }
    if (generation !== restoreGenerationRef.current) return;
    abortRef.current = sendChatMessageStream(
      {
        message: text,
        clientOperationId: userMessageId,
        conversationId: effectiveConversationId,
        authBearer,
        homeAccountId: account?.homeAccountId,
        username: account?.username,
        displayName: account?.name,
      },
      {
        onStart: (evt: StreamEvent) => {
          if (generation !== restoreGenerationRef.current) return;
          if (evt.conversationId) setConversationId(evt.conversationId);
        },
        onToken: (chunk, metadata) => {
          if (generation !== restoreGenerationRef.current) return;
          accumulatedReply += chunk;
          const receivedAt = Date.now();
          setMessages(prev =>
            prev.map(message =>
              message.id === replyId
                ? { ...message, text: message.text + chunk, receivedAt: message.receivedAt ?? receivedAt,
                    caseDetails: readCaseDetails(metadata?.caseDetails) ?? message.caseDetails }
                : message,
            ),
          );
        },
        onDone: (evt: StreamEvent) => {
          if (generation !== restoreGenerationRef.current) return;
          if (evt.conversationId) setConversationId(evt.conversationId);
          const doneText = accumulatedReply || evt.text || evt.reply || '';
          // Parse the agent's out-of-band signals (shared with ChatWidget): resolve citations (server →
          // Sources block → inline links) and pull [SUGGESTIONS: ...] pills + the invisible [RESOLVED]
          // marker off the body so the bubble shows clean prose and the pills render as clickable choices.
          const resolved = resolveCitations(evt.citations, doneText);
          const extracted = extractSuggestedPrompts(resolved.text);
          const finalText = stripResolvedTag(extracted.text).text;
          const suggestedPrompts = extracted.suggestedPrompts.length > 0 ? extracted.suggestedPrompts : undefined;
          const citations = resolved.citations.length > 0 ? resolved.citations : undefined;
          const isHandoff = evt.channelAction === 'handoff_to_omnichannel';
          setMessages(prev =>
            prev.map(message =>
              message.id === replyId
                ? {
                    ...message,
                    text: finalText,
                    streaming: false,
                    suggestedPrompts,
                    citations,
                    isHandoff,
                    receivedAt: message.receivedAt ?? Date.now(),
                  }
                : message,
            ),
          );
          if (evt.mode === 'prechat-survey' && evt.preChatSurvey) {
            // Two-phase survey handoff: render the pre-chat card; submitting it starts the
            // server-owned live session with the answers.
            const liveConversationId = evt.conversationId ?? conversationId ?? '';
            setServerPreChatSurvey({ survey: evt.preChatSurvey, conversationId: liveConversationId });
          } else if (evt.mode === 'live' && evt.sessionId) {
            // Handoff with no pre-chat survey. First transition into live mode for this conversation — the
            // AI just handed off. Call the SAME escalation entry point (POST /chat/init, action:"escalation")
            // a BYOA/static caller uses to actually connect to the human agent, passing the transcript so
            // far. Real HTTP round trip, not just an in-process marker — falls back to the existing
            // poll-driven flow (still starts the session on its own) if the call fails.
            const liveConversationId = evt.conversationId ?? conversationId ?? '';
            // An authoritative bridge decision already started the server session.
            const escalationInit = evt.liveSdkEnabled === false ? Promise.resolve(null)
              : callEscalationInit(liveConversationId, messagesRef.current);
            void escalationInit.then((escalation) => {
              if (generation !== restoreGenerationRef.current) return;
              if (escalation?.greeting) {
                appendSystemMessage(escalation.greeting);
              }
              enterServerLiveMode(escalation?.conversationId ?? evt.sessionId!, liveConversationId);
            });
          }
          setPending(false);
          // Skip the composer refocus when the reply presents choice pills — the focus effect moves
          // focus to the first pill instead so SR users land on the newly appeared card.
          if (!suggestedPrompts) inputRef.current?.focus();
          // Announce the completed agent reply once (streaming tokens are intentionally NOT announced).
          announce(finalText);
        },
        onLiveReady: (evt: StreamEvent) => {
          if (generation !== restoreGenerationRef.current) return;
          const liveConversationId = evt.conversationId ?? effectiveConversationId;
          if (evt.sessionId && liveConversationId) enterServerLiveMode(evt.sessionId, liveConversationId);
        },
        onError: (error: Error) => {
          if (generation !== restoreGenerationRef.current) return;
          if (isTerminalLiveFailure(error)) {
            setRecoveryState('failed');
            setLivePresentation(previous => reduceLivePresentation(previous, {
              liveCategory: 'error', recoveryState: 'failed',
            }));
          }
          if (isUncertainLiveSend(error)) {
            setMessages(prev => prev.filter(message => message.id !== replyId).map(message => message.id === userMessageId
              ? { ...message, ...uncertainLiveSendIdentity(error, message) } : message));
            setPending(false);
            inputRef.current?.focus();
            return;
          }
          const scanFeedback = liveMessageScanFeedback(error, EN_STRINGS);
          if (scanFeedback) {
            setMessages(prev => prev.filter(message => message.id !== userMessageId));
            setDraft(current => current || text);
          }
          const errorText = scanFeedback ?? `${teamsExperience.errorPrefix} ${friendlyStreamError(error)}`;
          setMessages(prev =>
            prev.map(message =>
              message.id === replyId
                ? { ...message, text: errorText, streaming: false, receivedAt: message.receivedAt ?? Date.now(),
                    ...(scanFeedback ? { role: 'system' as const } : {}) }
                : message,
            ),
          );
          setPending(false);
          inputRef.current?.focus();
          announce(errorText);
        },
      },
    );
  }, [account, acquireApiToken, callEscalationInit, conversationId, conversationScope, draft, isSignedIn, isAuthPending, mode, pending, restoring, serverPreChatSurvey, submittingServerPreChat, enterServerLiveMode, appendSystemMessage, announce, liveSendBlocked]);

  const sendText = useCallback((text: string) => void send(text), [send]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void send();
    }
  };

  const showQuickPrompts = messages.length === 1 && messages[0].id === 'welcome';
  const channelLabel = mode !== 'live' ? 'AI Agent'
    : livePresentation.phase === 'connecting' ? 'Connecting'
      : livePresentation.phase === 'waiting' ? 'Waiting'
        : livePresentation.phase === 'failed' ? 'Connection failed'
          : liveAgentName ?? EN_STRINGS.chrome.liveTitle;
  // Choice pills belong to the LAST message only: the moment the user answers (clicks a pill or
  // types), a user message becomes the tail and the pills retire immediately — never stale/duplicated.
  const tailMessage = messages[messages.length - 1];
  const latestSuggestedPrompts =
    tailMessage && tailMessage.role === 'agent' && !tailMessage.isLive
      ? tailMessage.suggestedPrompts ?? []
      : [];

  // When a completed agent response presents choice pills (e.g. "The app" / "Web browser", the
  // "which device" / troubleshoot cards), move keyboard/screen-reader focus to the first pill so SR
  // users land on the newly appeared card instead of being left on the composer (AB#6731542,
  // AB#6731551). onDone deliberately skips its composer refocus when the reply carries suggestions,
  // so this is the single source of focus for that case. Fires once per message.
  useEffect(() => {
    const showingSuggestions = latestSuggestedPrompts.length > 0 && !pending && mode === 'ai';
    if (!showingSuggestions) {
      focusedSuggestionsMsgIdRef.current = null;
      return;
    }
    const msgId = tailMessage?.id ?? null;
    if (!msgId || focusedSuggestionsMsgIdRef.current === msgId) return;
    focusedSuggestionsMsgIdRef.current = msgId;
    suggestedPromptsRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [latestSuggestedPrompts.length, pending, mode, tailMessage?.id]);

  // When the "Before I connect you" pre-chat survey appears, the composer footer is removed from the
  // DOM, so focus falls back to <body> and a screen-reader/keyboard user is silently dropped at the
  // top of the page with no cue the survey arrived. Move focus to the survey region (which announces
  // its label) once, mirroring the appeared-card focus behaviour (AB#6731542/AB#6731551, which note
  // the issue replicates wherever similar prompts appear). Reset the flag when the survey goes away.
  useEffect(() => {
    if (!serverPreChatSurvey) {
      preChatFocusedRef.current = false;
      return;
    }
    if (preChatFocusedRef.current) return;
    preChatFocusedRef.current = true;
    preChatRegionRef.current?.focus();
  }, [serverPreChatSurvey]);

  return (
    <div style={styles.page}>
      {/* Bypass block: lets keyboard/SR users jump past the header straight to the conversation
          (WCAG 2.4.1). Hidden off-screen until focused; targets the <main> region below. */}
      <a href="#teams-main" className="cmsp-skip-link">Skip to main content</a>
      <header style={styles.header}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="white" aria-hidden="true">
          <path d="M16.5 5.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm3.5 1h-1.2a2.8 2.8 0 0 1-4.6 0H13a1 1 0 0 0-1 1v4.5a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V7.5a1 1 0 0 0-1-1zM9.5 7a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zm2.5 1.5H7a2 2 0 0 0-2 2V16a1.5 1.5 0 0 0 1.5 1.5h6A1.5 1.5 0 0 0 14 16v-5.5a2 2 0 0 0-2-2z" />
        </svg>
        <h1 style={styles.title}>{teamsExperience.pageTitle}</h1>
        <span style={styles.badge}>{channelLabel}</span>
        <button type="button" ref={newChatRef} onClick={requestNewChat} style={styles.newChat}>
          + New Chat
        </button>
      </header>

      <main id="teams-main" tabIndex={-1} ref={scrollRef} style={styles.messages}>
        {messages.filter(message => !message.pendingSend).map(message => (
          <div
            key={message.id}
            data-live-read-id={message.isLive && !message.isBot ? message.messageId : undefined}
            style={styles.row(message.role)}
          >
            <div style={styles.bubble(message.role, message.isLive)}>
              {/* Visually-hidden speaker attribution so screen-reader users can tell whose turn each
                  message is (sighted users get this from bubble alignment/color). Live-agent messages
                  already render a visible "Name:" prefix, so we skip the generic agent label for those. */}
              {!message.isBot && !(message.isLive && (message.senderName || liveAgentName)) && (
                <span style={styles.srOnly}>
                  {message.role === 'user'
                    ? 'You said: '
                    : message.role === 'system'
                      ? 'System message: '
                      : 'Teams agent said: '}
                </span>
              )}
              {message.role === 'agent' ? (
                (message.text || message.liveCard || message.liveAuthCard || message.isHandoff || (message.liveAttachments && message.liveAttachments.length > 0) || (message.liveSuggestedActions && message.liveSuggestedActions.length > 0))
                  ? (
                    <>
                      {message.isBot && <strong>{EN_STRINGS.conversation.botLabel}: </strong>}
                      {message.isLive && (message.senderName || liveAgentName) && (
                        <strong>{message.senderName ?? liveAgentName}: </strong>
                      )}
                      {message.text && (
                        <CaseDetailsMessage text={message.isBot ? message.text : stripSignalsForDisplay(message.text)}
                          caseDetails={message.caseDetails} renderText={text => (
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{text}</ReactMarkdown>
                          )} />
                      )}
                      {message.streaming && <span style={styles.cursorBlink}>▊</span>}
                      {message.liveAttachments && message.liveAttachments.length > 0 && (
                        <LiveAttachmentChips
                          attachments={message.liveAttachments}
                          conversationId={conversationId ?? null}
                          getAuth={getAttachmentAuth}
                        />
                      )}
                      {message.role === 'agent' && !message.isLive && !message.streaming && message.citations && message.citations.length > 0 && (
                        <CitationList citations={message.citations} />
                      )}
                      {message.isHandoff && mode !== 'live' && (
                        <div style={styles.handoffCard}>
                          <div style={styles.handoffCardTitle}>Contact Support</div>
                          <div style={styles.handoffCardText}>Connecting you with a human support agent…</div>
                        </div>
                      )}
                      {message.liveCard != null && (
                        <AdaptiveCardBubble
                          card={message.liveCard}
                          disabled={mode !== 'live'}
                          onSubmit={(value) => void send(cardSubmitToText(value))}
                        />
                      )}
                      {message.liveAuthCard && (
                        <SignInCard text={message.liveAuthCard.text} url={message.liveAuthCard.url} disabled={mode !== 'live'} />
                      )}
                      {message.liveSuggestedActions && message.liveSuggestedActions.length > 0 && (
                        <SuggestedActionChips
                          actions={message.liveSuggestedActions}
                          disabled={mode !== 'live'}
                          onPick={(text) => void send(text)}
                        />
                      )}
                      {message.sentAt && message.receivedAt && (
                        <div style={styles.responseTime}>
                          <span style={styles.responseTimeLabel}>Time:</span>
                          <span style={styles.responseTimeValue}>{formatLatency(message.receivedAt - message.sentAt)}</span>
                        </div>
                      )}
                      {message.text && !message.streaming && (
                        <div style={styles.copyBar}>
                          <button
                            type="button"
                            aria-label={copiedMsgId === message.id ? 'Copied' : 'Copy'}
                            title={copiedMsgId === message.id ? 'Copied' : 'Copy'}
                            onClick={() => handleCopy(message.id)}
                            style={{
                              ...styles.copyButton,
                              ...(copiedMsgId === message.id ? styles.copyButtonActive : {}),
                            }}
                          >
                            {copiedMsgId === message.id ? (
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            ) : (
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  )
                  : pending ? (
                    <TypingDots />
                  ) : null
              ) : (
                message.text
              )}
              {message.deliveryUncertain && <div role="status" data-delivery-uncertain>{EN_STRINGS.status.sendStatusUnknown}</div>}
            </div>
          </div>
        ))}
        {showQuickPrompts && (
          <div style={styles.quickPrompts}>
            {QUICK_PROMPTS.map(quickPrompt => (
              <button
                key={quickPrompt.label}
                type="button"
                className="teams-pill"
                style={styles.quickButton}
                onClick={() => sendText(quickPrompt.prompt)}
              >
                {quickPrompt.label}
              </button>
            ))}
          </div>
        )}
        {latestSuggestedPrompts.length > 0 && !pending && mode === 'ai' && (
          <div ref={suggestedPromptsRef} style={styles.quickPrompts}>
            {latestSuggestedPrompts.map(prompt => (
              <button
                key={prompt}
                type="button"
                className="teams-pill"
                style={styles.quickButton}
                onClick={() => sendText(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>
        )}
      </main>

      {serverPreChatSurvey && (
        <div
          ref={preChatRegionRef}
          tabIndex={-1}
          role="region"
          aria-label="Before I connect you, pre-chat survey"
          style={styles.preChatRegion}
        >
          {serverPreChatError && <div role="alert">{serverPreChatError.text}</div>}
          <fieldset
            disabled={submittingServerPreChat || isAuthPending || serverPreChatError?.canRetry === false}
            style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
          >
            <PreChatSurveyCard
              survey={serverPreChatSurvey.survey}
              disabled={submittingServerPreChat || isAuthPending || serverPreChatError?.canRetry === false}
              onSubmit={(response) => void submitServerPreChatSurvey(response)}
            />
          </fieldset>
        </div>
      )}

      {serverPostChatSurvey && (
        <div style={styles.preChatRegion}>
          <div style={{ fontWeight: 700, color: '#242424', marginBottom: 4 }}>How was your support experience?</div>
          <div style={{ fontSize: 13, color: '#616161', marginBottom: 12 }}>
            Please take a moment to rate your conversation with the live agent.
          </div>
          <a href={serverPostChatSurvey.url} target="_blank" rel="noreferrer" style={styles.returnToAiButton}>
            Rate your experience
          </a>
        </div>
      )}

      {feedbackReason && (
        <FeedbackCard
          reason={feedbackReason}
          rating={feedbackRating}
          onRate={(rating) => completeFeedback(rating)}
          onSkip={() => completeFeedback()}
          onHover={setFeedbackRating}
        />
      )}

      {(!serverPreChatSurvey || submittingServerPreChat) && (
        <footer style={styles.composer}>
          <input
            ref={inputRef}
            className="teams-support-input"
            aria-label="Describe your Teams issue"
            value={draft}
            onChange={event => { setDraft(event.target.value); notifyTyping(); }}
            onKeyDown={onKeyDown}
            placeholder={mode === 'ended' ? 'This chat has ended. Start a new chat to continue.'
              : mode !== 'live' ? "Describe your Teams issue (e.g., 'Can't sign in on my iPhone')"
                : livePresentation.phase === 'connecting' ? 'Connecting to support...'
                  : livePresentation.phase === 'waiting' ? 'Message support while you wait...'
                    : livePresentation.phase === 'failed' ? 'Start a new chat to try again.'
                      : liveAgentName ? `Chat with ${liveAgentName}...` : 'Type a message...'}
            style={styles.input}
            disabled={pending || restoring || isAuthPending || submittingServerPreChat || mode === 'ended' || liveSendBlocked}
          />
          <button type="button" onClick={() => void send()} disabled={pending || restoring || isAuthPending || submittingServerPreChat || mode === 'ended' || liveSendBlocked || !draft.trim()} style={styles.send}>
            {teamsExperience.sendButtonLabel}
          </button>
        </footer>
      )}
      <div style={styles.disclaimer}>
        AI-generated content may be incorrect. Verify important information independently.
      </div>
      <footer style={styles.privacyFooter}>
        © {new Date().getFullYear()} - Microsoft Corporation |{' '}
        <a
          href="https://privacy.microsoft.com/privacystatement"
          target="_blank"
          rel="noreferrer"
          style={styles.privacyLink}
        >
          Privacy
        </a>
      </footer>
      {/* Dedicated polite live region: announces only completed messages/errors (not streaming tokens). */}
      <div role="status" aria-live="polite" aria-atomic="true" style={styles.srOnly}>
        {liveAnnouncement}
      </div>
    </div>
  );
}

/** Renders the agent's grounding sources behind a collapsible "Sources" hyperlink (parity with ChatWidget). */
function CitationList({ citations }: { citations: Citation[] }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 13, color: '#444', lineHeight: 1.5 }}>
        Learn more about where this information comes from in{' '}
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          aria-expanded={expanded}
          title={expanded ? 'Hide sources' : 'Show sources'}
          style={{
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            color: '#6264a7', textDecoration: 'underline', font: 'inherit',
          }}
        >
          Sources
        </button>.
      </div>
      {expanded && (
        <ol style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, lineHeight: 1.5 }}>
          {citations.map((c, i) => (
            <li key={i} style={{ marginBottom: 2 }}>
              {c.url ? (
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={c.snippet ?? c.url}
                  style={{ color: '#6264a7', textDecoration: 'none', wordBreak: 'break-word' }}
                >
                  {c.title || c.url}
                </a>
              ) : (
                <span title={c.snippet ?? undefined} style={{ color: '#374151' }}>{c.title}</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function TypingDots() {
  return (
    <span style={styles.typingDots} role="status" aria-label="Agent is typing">
      <span style={{ ...styles.typingDot, animationDelay: '0ms' }} />
      <span style={{ ...styles.typingDot, animationDelay: '200ms' }} />
      <span style={{ ...styles.typingDot, animationDelay: '400ms' }} />
    </span>
  );
}

function FeedbackCard({
  reason,
  rating,
  onRate,
  onSkip,
  onHover,
}: {
  reason: FeedbackReason;
  rating: number;
  onRate: (rating: number) => void;
  onSkip: () => void;
  onHover: (rating: number) => void;
}) {
  const title = reason === 'new-chat'
    ? 'Before starting a new chat, how was this conversation?'
    : 'How was your live support experience?';

  // Move keyboard focus into the revealed feedback content when it appears, so keyboard users
  // land on it directly instead of having to tab past unrelated controls to reach it.
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    sectionRef.current?.focus();
  }, []);

  // The star rating is a single-select control, so it must expose radiogroup/radio semantics rather
  // than a row of anonymous buttons (6731556/6731558: wrong role announced; 6731566: not selectable
  // under Talkback). Roving tabindex + arrow keys give it native radiogroup keyboard behaviour, and
  // selection follows focus so pressing Enter/Space (or a Talkback double-tap) commits the rating.
  const starGroupRef = useRef<HTMLDivElement>(null);
  const focusStar = (n: number) => {
    const clamped = Math.min(5, Math.max(1, n));
    onHover(clamped);
    starGroupRef.current?.querySelector<HTMLButtonElement>(`[data-star="${clamped}"]`)?.focus();
  };

  return (
    <section ref={sectionRef} tabIndex={-1} style={styles.feedbackCard} role="region" aria-label="Chat feedback">
      <div>
        <div style={styles.feedbackTitle}>{title}</div>
        <div style={styles.feedbackSubtitle}>Your rating helps improve Teams support.</div>
      </div>
      <div style={styles.feedbackActions}>
        <div
          ref={starGroupRef}
          role="radiogroup"
          aria-label="Rate this conversation from 1 to 5 stars"
          style={styles.starRow}
          onMouseLeave={() => onHover(0)}
        >
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              type="button"
              role="radio"
              data-star={star}
              aria-checked={star === rating}
              aria-label={`${star} star${star === 1 ? '' : 's'}`}
              tabIndex={star === (rating || 1) ? 0 : -1}
              style={styles.starButton(star <= rating)}
              onMouseEnter={() => onHover(star)}
              onFocus={() => onHover(star)}
              onClick={() => onRate(star)}
              onKeyDown={e => {
                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); focusStar((rating || 0) + 1); }
                else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); focusStar((rating || 1) - 1); }
              }}
            >
              ★
            </button>
          ))}
        </div>
        <button type="button" onClick={onSkip} style={styles.skipFeedbackButton}>
          Skip
        </button>
      </div>
    </section>
  );
}

const markdownComponents = {
  a: ({ children, href, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) =>
    href
      ? (
        <a {...props} href={href} target="_blank" rel="noopener noreferrer" style={styles.link}>
          {children}
        </a>
      )
      : (
        // No destination: render as plain text rather than an empty anchor (avoids anchor-no-href).
        <span>{children}</span>
      ),
  p: ({ children }: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p style={styles.markdownParagraph}>{children}</p>
  ),
  ul: ({ children }: React.HTMLAttributes<HTMLUListElement>) => (
    <ul style={styles.markdownList}>{children}</ul>
  ),
  ol: ({ children }: React.OlHTMLAttributes<HTMLOListElement>) => (
    <ol style={styles.markdownList}>{children}</ol>
  ),
  li: ({ children }: React.LiHTMLAttributes<HTMLLIElement>) => (
    <li style={styles.markdownListItem}>{children}</li>
  ),
};

function formatLatency(milliseconds: number): string {
  if (milliseconds < 1000) return `${Math.max(0, Math.round(milliseconds))}ms`;
  return `${(milliseconds / 1000).toFixed(1)}s`;
}

const teamsTheme = teamsExperience.theme;

const styles = {
  page: {
    minHeight: '100vh',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: teamsTheme.pageBackground,
    fontFamily: 'Segoe UI, system-ui, sans-serif',
    color: teamsTheme.text,
  } as React.CSSProperties,
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '16px 24px',
    background: teamsTheme.purpleMedium,
    color: '#fff',
    boxShadow: 'none',
  } as React.CSSProperties,
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1.2,
  } as React.CSSProperties,
  badge: {
    borderRadius: 10,
    padding: '2px 8px',
    background: 'rgba(0,0,0,0.25)',
    fontSize: 12,
  } as React.CSSProperties,
  newChat: {
    marginLeft: 'auto',
    padding: '6px 14px',
    border: '1px solid rgba(255,255,255,0.5)',
    borderRadius: 6,
    background: 'transparent',
    color: '#fff',
    font: 'inherit',
    fontSize: 13,
    cursor: 'pointer',
  } as React.CSSProperties,
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  } as React.CSSProperties,
  row: (role: Role): React.CSSProperties => ({
    display: 'flex',
    maxWidth: '75%',
    alignSelf: role === 'user' ? 'flex-end' : role === 'system' ? 'center' : 'flex-start',
  }),
  bubble: (role: Role, isLive?: boolean): React.CSSProperties => ({
    width: '100%',
    padding: role === 'system' ? '6px 12px' : '12px 16px',
    borderRadius: role === 'system'
      ? 16
      : role === 'user'
        ? `${12}px ${12}px ${4}px ${12}px`
        : `${12}px ${12}px ${12}px ${4}px`,
    background: role === 'system'
      ? '#e8e8e8'
      : role === 'user'
        ? teamsTheme.purpleMedium
        : isLive
          ? '#f0eefa'
          : '#fff',
    border: 'none',
    borderLeft: isLive ? `3px solid ${teamsTheme.purpleMedium}` : undefined,
    color: role === 'system' ? '#666' : role === 'user' ? '#fff' : '#1a1a1a',
    boxShadow: role === 'system' ? 'none' : '0 1px 3px rgba(0,0,0,0.12)',
    lineHeight: 1.6,
    fontSize: role === 'system' ? 12 : 14,
    wordBreak: 'break-word',
    whiteSpace: role === 'user' || role === 'system' ? 'pre-wrap' : undefined,
  }),
  composer: {
    display: 'flex',
    gap: 12,
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0',
    background: '#fff',
  } as React.CSSProperties,
  input: {
    flex: 1,
    border: '1px solid #d0d0d0',
    borderRadius: 8,
    padding: '12px 16px',
    font: 'inherit',
    fontSize: 14,
  } as React.CSSProperties,
  send: {
    border: 0,
    borderRadius: 8,
    padding: '12px 24px',
    background: teamsTheme.purpleMedium,
    color: '#fff',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
  } as React.CSSProperties,
  preChatRegion: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '0 24px 16px',
    background: '#fff',
  } as React.CSSProperties,
  returnToAiButton: {
    alignSelf: 'flex-start',
    border: `1px solid ${teamsTheme.purpleMedium}`,
    borderRadius: 8,
    padding: '9px 14px',
    background: '#fff',
    color: teamsTheme.purpleMedium,
    font: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  } as React.CSSProperties,
  feedbackCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '14px 24px',
    background: '#fff',
    borderTop: '1px solid #e0e0e0',
    boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
  } as React.CSSProperties,
  feedbackTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: teamsTheme.text,
  } as React.CSSProperties,
  feedbackSubtitle: {
    marginTop: 2,
    fontSize: 12,
    color: '#666',
  } as React.CSSProperties,
  feedbackActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  } as React.CSSProperties,
  starRow: {
    display: 'flex',
    gap: 2,
  } as React.CSSProperties,
  starButton: (active: boolean): React.CSSProperties => ({
    border: 0,
    background: 'transparent',
    color: active ? '#f5b301' : '#c8c8c8',
    cursor: 'pointer',
    fontSize: 24,
    lineHeight: 1,
    padding: '2px 3px',
  }),
  skipFeedbackButton: {
    border: '1px solid #d0d0d0',
    borderRadius: 8,
    background: '#fff',
    color: '#444',
    cursor: 'pointer',
    font: 'inherit',
    fontSize: 13,
    padding: '7px 12px',
  } as React.CSSProperties,
  link: {
    color: teamsTheme.purpleMedium,
    textDecoration: 'underline',
  } as React.CSSProperties,
  markdownParagraph: {
    margin: '2px 0',
  } as React.CSSProperties,
  markdownList: {
    margin: '4px 0',
    paddingLeft: 22,
  } as React.CSSProperties,
  markdownListItem: {
    margin: '2px 0',
  } as React.CSSProperties,
  typingDots: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '2px 0',
  } as React.CSSProperties,
  typingDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: '#999',
    display: 'inline-block',
    animation: 'cmsp-chat-bounce 1.4s infinite',
  } as React.CSSProperties,
  cursorBlink: {
    display: 'inline-block',
    marginLeft: 2,
    color: teamsTheme.purpleMedium,
  } as React.CSSProperties,
  responseTime: {
    display: 'inline-flex',
    gap: 3,
    marginTop: 6,
    paddingTop: 6,
    borderTop: '1px solid #f0f0f0',
    color: '#888',
    fontSize: 11,
  } as React.CSSProperties,
  responseTimeLabel: {
    color: '#737373',
  } as React.CSSProperties,
  responseTimeValue: {
    color: '#555',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  } as React.CSSProperties,
  copyBar: {
    display: 'flex',
    gap: 4,
    marginTop: 6,
  } as React.CSSProperties,
  copyButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 6,
    padding: '2px 6px',
    cursor: 'pointer',
    color: '#555',
    opacity: 0.6,
    transition: 'opacity 0.15s, background 0.15s, border-color 0.15s',
  } as React.CSSProperties,
  copyButtonActive: {
    opacity: 1,
    background: '#f0eefa',
    borderColor: teamsTheme.purpleMedium,
    color: teamsTheme.purpleMedium,
  } as React.CSSProperties,
  quickPrompts: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignSelf: 'flex-start',
    padding: '4px 0',
  } as React.CSSProperties,
  quickButton: {
    padding: '8px 14px',
    background: '#f0eefa',
    border: '1px solid #d8d6f3',
    borderRadius: 20,
    color: teamsTheme.purpleDark,
    font: 'inherit',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  } as React.CSSProperties,
  handoffCard: {
    marginTop: 8,
    padding: '10px 12px',
    background: '#f0eefa',
    border: '1px solid #d8d6f3',
    borderRadius: 8,
  } as React.CSSProperties,
  handoffCardTitle: {
    fontWeight: 700,
    fontSize: 13,
    color: teamsTheme.purpleDark,
    marginBottom: 2,
  } as React.CSSProperties,
  handoffCardText: {
    fontSize: 13,
    color: '#424242',
  } as React.CSSProperties,
  disclaimer: {
    padding: '6px 24px',
    background: '#f9f9f9',
    borderTop: '1px solid #eee',
    color: '#6b6b6b',
    fontSize: 11,
    textAlign: 'center',
  } as React.CSSProperties,
  privacyFooter: {
    padding: '12px 24px',
    background: teamsTheme.purpleMedium,
    color: '#fff',
    fontSize: 12,
    textAlign: 'center',
  } as React.CSSProperties,
  privacyLink: {
    color: '#fff',
    textDecoration: 'underline',
  } as React.CSSProperties,
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  } as React.CSSProperties,
};
